import TextField from "@/components/UI/TextField/TextField";
import classNames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faBrowser, faCodeCompare, faCrop } from "@fortawesome/pro-regular-svg-icons";
import Select from "@/components/UI/Select/Select";
import { toSelectOptions } from "@/utils/cms";
import { ButtonCreatorConfig } from "./utils";
import Simulator from "./Simulator";

interface MonitorPageProps extends React.HTMLAttributes<HTMLElement> {
  onSettingsChange?: (nextSettings: ButtonCreatorConfig) => void;
  section: any;
  settings?: ButtonCreatorConfig;
}

const MonitorPage = ({ className, onSettingsChange, section, settings, ...props }: MonitorPageProps) => {
  return (
    <div className={classNames("flex divide-x", className)} {...props}>
      <div className="w-2/3 divide-y">
        <div className="flex flex-col divide-y" {...props}>
          <div className="flex gap-12 p-12">
            <div className="flex flex-1 gap-4">
              <FontAwesomeIcon className="fa-fw text-2xl text-vpBlue-500" icon={faBell} />
              <div className="flex flex-1 flex-col gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <label htmlFor={section?.ButtonThresholdSelect?.Name} className="font-bold">
                    {section?.ButtonThresholdSelect?.Label}
                  </label>
                  <div className="text-vpGray-700">{section?.ButtonThresholdSelect?.HelperText}</div>
                </div>
                <Select
                  name={section?.ButtonThresholdSelect?.Name}
                  id={section?.ButtonThresholdSelect?.HtmlId}
                  onChange={(nextValue: string) => {
                    onSettingsChange?.({
                      threshold: nextValue,
                    });
                  }}
                  options={toSelectOptions(section.ButtonThresholdSelect)?.map((option: any) => {
                    return {
                      children: option?.label,
                      value: option?.value ?? option?.label,
                    };
                  })}
                  size="large"
                  value={settings?.threshold}
                />
              </div>
            </div>
            <div className="flex flex-1 gap-4">
              <FontAwesomeIcon className="fa-fw text-2xl text-vpBlue-500" icon={faCodeCompare} />
              <div className="flex flex-1 flex-col gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <label htmlFor={section?.ButtonCompareTypeSelect?.Name} className="font-bold">
                    {section?.ButtonCompareTypeSelect?.Label}
                  </label>
                  <div className="text-vpGray-700">{section?.ButtonCompareTypeSelect?.HelperText}</div>
                </div>
                <Select
                  name={section?.ButtonCompareTypeSelect?.Name}
                  id={section?.ButtonCompareTypeSelect?.HtmlId}
                  onChange={(nextValue: string) => {
                    onSettingsChange?.({
                      compareType: nextValue,
                    });
                  }}
                  options={toSelectOptions(section.ButtonCompareTypeSelect)?.map((option: any) => {
                    return {
                      children: option?.label,
                      value: option?.value ?? option?.label,
                    };
                  })}
                  size="large"
                  value={settings?.compareType}
                />
              </div>
            </div>
          </div>
          <div className="p-12">
            <div className="flex flex-1 gap-4">
              <FontAwesomeIcon className="fa-fw text-2xl text-vpBlue-500" icon={faCrop} />
              <div className="flex flex-1 flex-col gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <div className="font-bold">{section?.CropSectionTitle}</div>
                  <div className="text-vpGray-700">{section?.CropSectionSubtitle}</div>
                </div>
                <div className="flex flex-col gap-10 lg:flex-row">
                  <div className="flex flex-1 flex-col gap-2">
                    <label htmlFor={section?.ButtonCropXTextField?.Name} className="font-medium">
                      {section?.ButtonCropXTextField?.Label}
                    </label>
                    <TextField
                      id={section?.ButtonCropXTextField?.HtmlId}
                      name={section?.ButtonCropXTextField?.Name}
                      onChange={(event) => {
                        onSettingsChange?.({
                          crop: {
                            ...settings?.crop,
                            x: event.currentTarget.value,
                          },
                        });
                      }}
                      placeholder={section?.ButtonCropXTextField?.Placeholder}
                      size="large"
                      value={String(settings?.crop?.x)}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2">
                    <label htmlFor={section?.ButtonCropYTextField?.Name} className="font-medium">
                      {section?.ButtonCropYTextField?.Label}
                    </label>
                    <TextField
                      id={section?.ButtonCropYTextField?.HtmlId}
                      name={section?.ButtonCropYTextField?.Name}
                      onChange={(event) => {
                        onSettingsChange?.({
                          crop: {
                            ...settings?.crop,
                            y: event.currentTarget.value,
                          },
                        });
                      }}
                      placeholder={section?.ButtonCropYTextField?.Placeholder}
                      size="large"
                      value={String(settings?.crop?.y)}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2">
                    <label htmlFor={section?.ButtonCropWidthTextField?.Name} className="font-medium">
                      {section?.ButtonCropWidthTextField?.Label}
                    </label>
                    <TextField
                      id={section?.ButtonCropWidthTextField?.HtmlId}
                      name={section?.ButtonCropWidthTextField?.Name}
                      onChange={(event) => {
                        onSettingsChange?.({
                          crop: {
                            ...settings?.crop,
                            width: event.currentTarget.value,
                          },
                        });
                      }}
                      placeholder={section?.ButtonCropWidthTextField?.Placeholder}
                      size="large"
                      value={String(settings?.crop?.width)}
                    />
                  </div>
                  <div className="flex flex-1 flex-col gap-2">
                    <label htmlFor={section?.ButtonCropHeightTextField?.Name} className="font-medium">
                      {section?.ButtonCropHeightTextField?.Label}
                    </label>
                    <TextField
                      id={section?.ButtonCropHeightTextField?.HtmlId}
                      name={section?.ButtonCropHeightTextField?.Name}
                      onChange={(event) => {
                        onSettingsChange?.({
                          crop: {
                            ...settings?.crop,
                            height: event.currentTarget.value,
                          },
                        });
                      }}
                      placeholder={section?.ButtonCropHeightTextField?.Placeholder}
                      size="large"
                      value={String(settings?.crop?.height)}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="p-12">
            <div className="flex flex-1 gap-4">
              <FontAwesomeIcon className="fa-fw text-2xl text-vpBlue-500" icon={faBrowser} />
              <div className="flex flex-1 flex-col gap-4">
                <div className="flex flex-1 flex-col gap-2">
                  <label htmlFor={section?.ButtonUrlTextField?.Name} className="font-bold">
                    {section?.ButtonUrlTextField?.Label}
                  </label>
                  <div className="text-vpGray-700">{section?.ButtonUrlTextField?.HelperText}</div>
                </div>
                <TextField
                  id={section?.ButtonUrlTextField?.HtmlId}
                  name={section?.ButtonUrlTextField?.Name}
                  onChange={(event) => {
                    onSettingsChange?.({
                      url: event.currentTarget.value,
                    });
                  }}
                  placeholder={section?.ButtonUrlTextField?.Placeholder}
                  size="large"
                  value={settings?.url}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <Simulator
        className="w-1/3"
        widgetProps={{
          id: "monitor-page-widget",
          ...settings,
        }}
      />
    </div>
  );
};

export default MonitorPage;
